import { Fragment, useState, useEffect } from "react";
import { FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import { FaCheck, FaMinus } from "react-icons/fa6";
import { IoSparklesSharp } from "react-icons/io5";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/* -------------------------------------------------------------------------- */
/* Utility                                                                    */
/* -------------------------------------------------------------------------- */
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------------------------- */
/* Feature with Tooltip Component                                             */
/* -------------------------------------------------------------------------- */
function FeatureWithTooltip({ feature, children, showMobileIcon = true, openMobileTooltip, setOpenMobileTooltip, tierId }) {
  if (!feature.description) {
    return children;
  }

  const tooltipId = `${tierId}-${feature.name}`;
  const isOpen = openMobileTooltip === tooltipId;

  // Auto-close tooltip after 5 seconds on mobile
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setOpenMobileTooltip(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, setOpenMobileTooltip]);

  const handleMobileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMobileTooltip(isOpen ? null : tooltipId);
  };

  const handleMobileTooltipOpenChange = (open) => {
    if (!open) setOpenMobileTooltip(null);
  };

  return (
    <>
      {/* Desktop tooltip */}
      <div className="hidden lg:block">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-block">
              {children}
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs" side="right">
            {feature.description}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Mobile tooltip */}
      <div className="lg:hidden">
        <Tooltip open={isOpen} onOpenChange={handleMobileTooltipOpenChange}>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-full text-left flex items-center gap-1 touch-manipulation"
              aria-label={`${feature.name} - tap for more info`}
              onClick={handleMobileClick}
            >
              <span className="inline-block">
                {children}
              </span>
             
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs" side="top">
            {feature.description}
          </TooltipContent>
        </Tooltip>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Pricing component                                                          */
/* -------------------------------------------------------------------------- */
export default function Pricing({ data, subtitle }) {
  const [openMobileTooltip, setOpenMobileTooltip] = useState(null);
  
  if (!data) return null;               // safety‑net for bad props

  const { service, tiers, sections } = data;

  return (
      <div className="my-16 bg-white scroll-m-32" id="pricing">
      {/* ---------- Header ---------- */}
      <div className="mb-6 flex flex-col items-center gap-3 lg:gap-6 px-5">
        <h2 className="items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-vblue3 lg:-mb-1 lg:text-base text-center px-2">
          <IoSparklesSharp className="mr-1.5 inline" />
          {service.tagline ? service.tagline : `${service.title} Agency`}
        </h2>

        <h2 className="text-center text-3xl font-bold tracking-tight lg:text-4xl">
          {service.title} Pricing
        </h2>

        {service.description && (
          <p className="mt-1 max-w-lg text-center text-base text-gray-600">
            {service.description}
          </p>
        )}
      </div>

      {/* ---------------- Mobile stack ---------------- */}
      <div className="mx-auto max-w-screen-xl px-5">
        <div className="mx-auto max-w-md space-y-8 sm:mt-16 lg:hidden">
          {tiers.map((tier) => (
            <section
              key={tier.id}
              className={classNames(
                tier.mostPopular
                  ? "rounded-xl bg-gray-400/5 ring-1 ring-inset ring-gray-200 p-4"
                  : "",
                "",
              )}
            >
              <h3 id={tier.id} className="text-sm/6 font-semibold text-gray-900">
                {tier.name}
              </h3>

              <p className="mt-2 flex items-baseline gap-x-1 text-gray-900">
                <span className="text-4xl font-semibold">{tier.price}</span>
              </p>

              {tier.setup && (
                <p className="text-xs mt-1 text-neutral-500 italic">
                  Setup {tier.setup}
                </p>
              )}

              <p className="text-sm opacity-70 mt-4">
                {tier.description}
              </p>

              <ul role="list" className="mt-10 space-y-4 text-sm/6 text-gray-900">
                {sections.map((section) => (
                  <li key={section.name}>
                    <ul role="list" className="space-y-4">
                      {section.features.map((feature) =>
                        feature.tiers[tier.name] ? (
                          <li key={feature.name} className="flex gap-x-3">
                             <FeatureWithTooltip 
                              feature={feature}
                              openMobileTooltip={openMobileTooltip}
                              setOpenMobileTooltip={setOpenMobileTooltip}
                              tierId={tier.id}
                            ><FaInfoCircle
                              aria-hidden="true"
                              className="h-6 w-5 inline-block mr-2 flex-none text-neutral-400 relative bottom-px"
                            />
                           
                              <span>
                                {feature.name}{" "}
                                {typeof feature.tiers[tier.name] === "string" && (
                                  <span className="text-sm/6 text-gray-500">
                                    ({feature.tiers[tier.name]})
                                  </span>
                                )}
                              </span>
                            </FeatureWithTooltip>
                          </li>
                        ) : null,
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* ---------------- Desktop table ---------------- */}
        <div className="isolate mt-12 hidden lg:block">
          <div className="relative -mx-8">
            {tiers.some((tier) => tier.mostPopular) && (
              <div className="absolute inset-x-4 inset-y-0 -z-10 flex">
                <div
                  style={{
                    marginLeft: `${(tiers.findIndex((t) => t.mostPopular) + 1) * 25}%`,
                  }}
                  aria-hidden="true"
                  className="flex w-1/4 px-4"
                >
                  <div className="w-full rounded-t-xl border-x border-t border-gray-900/10 bg-gray-400/5" />
                </div>
              </div>
            )}

            <table className="w-full table-fixed border-separate border-spacing-x-8 text-left">
              <caption className="sr-only">Pricing plan comparison</caption>
              <colgroup>
                <col className="w-1/4" />
                <col className="w-1/4" />
                <col className="w-1/4" />
                <col className="w-1/4" />
              </colgroup>

              <thead>
                <tr>
                  <td />
                  {tiers.map((tier) => (
                    <th
                      key={tier.id}
                      scope="col"
                      className="px-6 pt-6 xl:px-8 xl:pt-8"
                    >
                      <div className="text-sm/7 font-semibold text-gray-900">
                        {tier.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                <tr>
                  <th scope="row">
                    <span className="sr-only">Price</span>
                  </th>
                  {tiers.map((tier) => (
                    <td key={tier.id} className="px-6 pt-2 xl:px-8">
                      <div className="flex flex-col items-start gap-y-1 text-gray-900">
                        <span className="text-4xl font-semibold">{tier.price}</span>
                        {tier.setup &&
                        <span className="text-xs mt-1 text-neutral-500 italic">
                          Setup {tier.setup}
                        </span>
                        }
                        <p className="text-sm opacity-70 mt-4">
                          {tier.description}
                        </p>
                      </div>
                    </td>
                  ))}
                </tr>

                {sections.map((section, sectionIdx) => (
                  <Fragment key={section.name}>
                    <tr>
                      <th
                        scope="colgroup"
                        colSpan={4}
                        className={classNames(
                          sectionIdx === 0 ? "" : "pt-16",
                          "pb-4 text-s font-semibold text-gray-900",
                        )}
                      >
                        {section.name}
                        <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/10" />
                      </th>
                    </tr>

                    {section.features.map((feature) => (
                      <tr key={feature.name}>
                        <th
                          scope="row"
                          className="py-4 text-sm/6 font-normal text-gray-900"
                        >
                          <FeatureWithTooltip 
                            feature={feature} 
                            showMobileIcon={false}
                            openMobileTooltip={openMobileTooltip}
                            setOpenMobileTooltip={setOpenMobileTooltip}
                            tierId={null}
                          >
                            <span className="transition-colors">
                              {feature.name}
                            </span>
                          </FeatureWithTooltip>
                          <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/5" />
                        </th>

                        {tiers.map((tier) => (
                          <td key={tier.id} className="px-6 py-4 xl:px-8">
                            {typeof feature.tiers[tier.name] === "string" ? (
                              <div className="text-center text-sm/6 text-gray-500">
                                {feature.tiers[tier.name]}
                              </div>
                            ) : feature.tiers[tier.name] === true ? (
                              <FaCheckCircle
                                aria-hidden="true"
                                className="mx-auto size-5 text-vblue3"
                              />
                            ) : (
                              <FaMinus
                                aria-hidden="true"
                                className="mx-auto size-5 text-gray-400"
                              />
                            )}

                            <span className="sr-only">
                              {feature.tiers[tier.name] === true
                                ? "Included"
                                : "Not included"}{" "}
                              in {tier.name}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    );
}
